import { motion } from 'motion/react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { Button, XStack, YStack } from '@/components/ui';
import { cn } from '@/lib/utils';

import { PREPARE_STEPS } from './feed-onboarding.config';
import { useFocusOnMount, usePrepareProgress } from './feed-onboarding.hooks';

const RING_RADIUS = 56;
const RING_STROKE_WIDTH = 3;

const ProgressRing = ({ progress }: { progress: number | null }) => (
  <XStack center className="relative size-32">
    <svg viewBox="0 0 120 120" className="absolute inset-0 -rotate-90">
      <circle
        cx="60"
        cy="60"
        r={RING_RADIUS}
        fill="none"
        strokeWidth={RING_STROKE_WIDTH}
        className="stroke-border-strong"
      />
      {progress !== null && (
        <motion.circle
          cx="60"
          cy="60"
          r={RING_RADIUS}
          fill="none"
          strokeWidth={RING_STROKE_WIDTH}
          strokeLinecap="round"
          className="stroke-brand-cyan-cornflower-blue"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: progress }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      )}
    </svg>
    <AppLogo size={40} />
  </XStack>
);

const PrepareChecklist = ({ activeIndex }: { activeIndex: number }) => (
  <YStack className="w-full max-w-64 gap-3" aria-live="polite">
    {PREPARE_STEPS.map((label, index) => {
      const isDone = index < activeIndex;
      const isActive = index === activeIndex;
      return (
        <XStack
          key={label}
          className={cn(
            'items-center gap-3 text-left text-base transition-colors duration-300',
            isDone || isActive ? 'text-foreground' : 'text-foreground-subtle'
          )}
        >
          {isDone ? (
            <Icons.check className="size-4 shrink-0 text-brand-dark-emerald-green dark:text-brand-emerald-green" />
          ) : isActive ? (
            <Icons.loader className="size-4 shrink-0 animate-spin text-brand-cyan-cornflower-blue motion-reduce:animate-none" />
          ) : (
            <span className="size-4 shrink-0 rounded-full border-[1.5px] border-border-strongest" />
          )}
          {label}
        </XStack>
      );
    })}
  </YStack>
);

export const PrepareStep = ({
  hasConnectedApps,
  isFinishing,
  hasFinishFailed,
  onFinish,
}: {
  hasConnectedApps: boolean;
  isFinishing: boolean;
  hasFinishFailed: boolean;
  onFinish: () => void;
}) => {
  const activeIndex = usePrepareProgress();
  const headingRef = useFocusOnMount<HTMLHeadingElement>();

  return (
    <YStack className="min-h-0 flex-1 gap-4">
      <YStack className="-mx-6 min-h-0 flex-1 overflow-y-auto px-6">
        <YStack className="my-auto items-center gap-6 py-4 text-center">
          <ProgressRing
            progress={
              hasConnectedApps ? activeIndex / PREPARE_STEPS.length : null
            }
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="flex flex-col items-center gap-2"
          >
            <h1
              ref={headingRef}
              tabIndex={-1}
              className="font-serif text-2xl leading-tight tracking-tight outline-none md:text-3xl"
            >
              {hasConnectedApps ? 'Preparing your feed' : 'You’re all set'}
            </h1>
            <p className="max-w-md text-pretty text-base leading-normal text-foreground-subtle">
              {hasConnectedApps
                ? 'Littlebird is reading the apps you connected. This takes a few minutes, and you can start looking around now.'
                : 'Connect an app from Integrations whenever you like, and Littlebird will start filling your feed.'}
            </p>
          </motion.div>
          {hasConnectedApps && <PrepareChecklist activeIndex={activeIndex} />}
        </YStack>
      </YStack>
      <YStack className="items-center gap-3">
        <Button
          size="lg"
          textSize="base"
          className="h-12 w-full px-8 focus-ring md:w-auto"
          isLoading={isFinishing}
          onClick={onFinish}
        >
          Start using Littlebird
        </Button>
        {hasFinishFailed && (
          <p role="alert" className="text-center text-sm text-danger-11">
            Something went wrong finishing up. Please try again.
          </p>
        )}
      </YStack>
    </YStack>
  );
};
